import Typography from '@mui/material/Typography';
import { SelectOption } from 'atoms/Select';
import React, { ReactNode, useEffect, useState } from 'react';
import {
  calcInvestmentDetails,
  EMPTY_VALUE,
  formatAmount,
  formatHoldingsInIssuer,
  formatQuantity,
  formatSharePremium,
  formatSharePrice,
  formatValuation
} from 'stores/financeUtils';
import { IOfferingData } from 'stores/interfaces';
import {
  DocumentStatus,
  DocumentType,
  EventStatus,
  FeeType,
  IHolding,
  ImportStatus,
  IOffering,
  IOrder,
  IReport,
  ISponsor,
  ITrade,
  OfferingStatus,
  OfferingType,
  OrderStatus,
  OrderType,
  PermissionType,
  ReportInterval,
  ReportStatus,
  ReportType,
  TradeStatus,
  TransactionType,
  VisibilityType
} from 'stores/networkInterfaces';
import { IAuthDetails } from 'utils/authUtils';
import {
  formatDate,
  formatHoldPeriod,
  FULL_DATE_FORMAT,
  FULL_DATE_TIME_FORMAT
} from 'utils/dateUtils';
import { IOfferingBrowsingData, IOfferingInfo, IResourceData } from 'utils/globalTypes';
import { formatNumber, roundToOne, toPercentageFixedFormat, toPercentageFormat } from 'utils/math';
import { getGalleryItems, getObjProperty, isNotEmpty, isNumeric } from 'utils/object';
import { getValidUrl } from 'utils/urlUtils';

export interface OfferingItem {
  title?: string;
  shortTitle?: string;
  description?: string;
  value?: ReactNode;
  range?: ReactNode;
}

const getValue = (
  value: number | undefined,
  formatValue: (value?: number) => string,
  defaultValue = EMPTY_VALUE
) => (!!value ? formatValue(value) : defaultValue);

const getRangeValue = (
  value: number | undefined,
  topValue: number | undefined,
  formatValue: (value?: number) => string,
  defaultValue = undefined
) => (!!value && !!topValue ? `${formatValue(value)} - ${formatValue(topValue)}` : defaultValue);

export const useCanCancelOrder = (order?: IOrder) => {
  const [canCancel, setCanCancel] = useState<boolean>(false);

  useEffect(() => {
    setCanCancel(
      !order ||
        (order.order_status != OrderStatus.canceled &&
          order.order_status != OrderStatus.matched &&
          order.order_status != OrderStatus.executing &&
          order.order_status != OrderStatus.executed)
    );
  }, [order]);

  return canCancel;
};

export const useCanPromoteOrder = (order?: IOrder) => {
  const [canPromote, setCanPromote] = useState<boolean>(false);

  useEffect(() => {
    setCanPromote(
      !order ||
        order.order_status == OrderStatus.active ||
        order.order_status == OrderStatus.matched ||
        order.order_status == OrderStatus.executing
    );
  }, [order]);

  return canPromote;
};

export const useCanViewOrderDocument = (order?: IOrder, system?: boolean) => {
  const [canShowDocument, setCanShowDocument] = useState<boolean>(false);

  useEffect(() => {
    setCanShowDocument(
      !!order &&
        ((system && !!order.document_id && !!order.document_sign_date) ||
          (!system && !!order.document_id && order.order_status != OrderStatus.canceled))
    );
  }, [order, system]);

  return canShowDocument;
};

export const useCanCancelTrade = (trade?: ITrade) => {
  const [canCancel, setCanCancel] = useState<boolean>(false);

  useEffect(() => {
    setCanCancel(
      trade?.trade_status !== undefined &&
        [TradeStatus.pending, TradeStatus.pending_executing, TradeStatus.executing].includes(
          trade.trade_status
        )
    );
  }, [trade]);

  return canCancel;
};

export const useCanExecuteTrade = (trade?: ITrade) => {
  const [canExecute, setCanExecute] = useState<boolean>(false);

  useEffect(() => {
    setCanExecute(
      trade?.trade_status !== undefined &&
        [TradeStatus.pending].includes(trade.trade_status) &&
        !!trade?.buyer_aml_id &&
        !!trade?.seller_aml_id
    );
  }, [trade]);

  return canExecute;
};

export const useCanFinalizeTrade = (trade?: ITrade) => {
  const [canFinalize, setCanFinalize] = useState<boolean>(false);

  useEffect(() => {
    setCanFinalize(
      trade?.trade_status !== undefined &&
        [TradeStatus.executing].includes(trade.trade_status) &&
        !!trade?.buyer_aml_id &&
        !!trade?.seller_aml_id
    );
  }, [trade]);

  return canFinalize;
};

export const useCanApproveOrder = (order?: IOrder, management?: boolean) => {
  const [canApprove, setCanApprove] = useState<boolean>(false);

  useEffect(() => {
    setCanApprove(
      !!order &&
        order.order_status == OrderStatus.pending &&
        !!(management && order.sponsor_approval_id && !order.sponsor_approval_date)
    );
  }, [order, management]);

  return canApprove;
};

export const useCanMatchOrder = (order?: IOrder, system?: boolean) => {
  const [canMatch, setCanMatch] = useState<boolean>(false);

  useEffect(() => {
    setCanMatch(!!(system && !!order && order.order_status == OrderStatus.active));
  }, [order, system]);

  return canMatch;
};

export const useCanEditOrder = (order?: IOrder, system?: boolean) => {
  const [canEdit, setCanEdit] = useState<boolean>(false);

  useEffect(() => {
    setCanEdit(
      !!order &&
        order.order_status !== undefined &&
        (system
          ? [
              OrderStatus.pending,
              OrderStatus.active,
              OrderStatus.matched,
              OrderStatus.executing
            ].includes(order.order_status)
          : [OrderStatus.pending, OrderStatus.active].includes(order.order_status))
    );
  }, [order, system]);

  return canEdit;
};

export const useCanEditTrade = (trade?: ITrade) => {
  const [canEdit, setCanEdit] = useState<boolean>(false);

  useEffect(() => {
    setCanEdit(
      !!trade &&
        trade.trade_status !== undefined &&
        [TradeStatus.pending].includes(trade.trade_status)
    );
  }, [trade]);

  return canEdit;
};

export const getDocumentStatusVal = (value?: string) => {
  switch (value) {
    case 'pending':
      return DocumentStatus.pending.valueOf();
    case 'done':
      return DocumentStatus.done.valueOf();
    case 'canceled':
      return DocumentStatus.canceled.valueOf();
    default:
      return undefined;
  }
};

export const getDocumentStatusStr = (value?: DocumentStatus) => {
  switch (value) {
    case DocumentStatus.pending.valueOf():
      return 'Pending';
    case DocumentStatus.done.valueOf():
      return 'Done';
    case DocumentStatus.canceled.valueOf():
      return 'Canceled';
    default:
      return '';
  }
};

export const getDocumentTypeStr = (value?: DocumentType) => {
  switch (value) {
    case DocumentType.docusign.valueOf():
      return 'Docusign';
    case DocumentType.resource.valueOf():
      return 'Resource';
    default:
      return '';
  }
};

export const getTransactionTypeVal = (value?: string) => {
  switch (value) {
    case 'distributions':
      return TransactionType.distributions.valueOf();
    case 'holding_buy':
      return TransactionType.holdings_buy.valueOf();
    case 'holding_sell':
      return TransactionType.holdings_sell.valueOf();
    case 'management_fees':
      return TransactionType.management_fees.valueOf();
    default:
      return undefined;
  }
};

export const getPermissionTypeStr = (value?: PermissionType) => {
  switch (value) {
    case PermissionType.documents.valueOf():
      return 'Access Documents';
    case PermissionType.offering.valueOf():
      return 'Access Offering';
    default:
      return '';
  }
};

export const getFeeTypeStr = (value?: FeeType) => {
  switch (value) {
    case FeeType.offering_buyer_fees.valueOf():
      return 'Buyer Fee';
    case FeeType.offering_seller_fees.valueOf():
      return 'Seller Fee';
    default:
      return '';
  }
};

export const getTransactionTypeStr = (value?: TransactionType) => {
  switch (value) {
    case TransactionType.distributions.valueOf():
      return 'Distributions Received';
    case TransactionType.holdings_buy.valueOf():
      return 'Holdings Bought';
    case TransactionType.holdings_sell.valueOf():
      return 'Holdings Sold';
    case TransactionType.management_fees.valueOf():
      return 'Management Fees';
    default:
      return '';
  }
};

export const getOrderTypeVal = (value?: number | string) => {
  switch (value) {
    case OrderType[OrderType.int_buy]:
    case OrderType.int_buy.valueOf():
    case String(OrderType.int_buy.valueOf()):
      return OrderType.int_buy;
    case OrderType[OrderType.bid_buy]:
    case OrderType.bid_buy.valueOf():
    case String(OrderType.bid_buy.valueOf()):
      return OrderType.bid_buy;
    case OrderType[OrderType.bid_sell]:
    case OrderType.bid_sell.valueOf():
    case String(OrderType.bid_sell.valueOf()):
      return OrderType.bid_sell;
    default:
      return undefined;
  }
};

export const getOrderTypeStr = (value?: OrderType) => {
  switch (value) {
    case OrderType.int_buy.valueOf():
      return 'Buy';
    case OrderType.bid_buy.valueOf():
      return 'Buy';
    case OrderType.bid_sell.valueOf():
      return 'Sell';
    default:
      return '';
  }
};

export const getAdminOrderTypeStr = (value?: OrderType) => {
  switch (value) {
    case OrderType.int_buy.valueOf():
      return 'Buy';
    case OrderType.bid_buy.valueOf():
      return 'Secondary Buy';
    case OrderType.bid_sell.valueOf():
      return 'Secondary Sell';
    default:
      return '';
  }
};

export const getOrderStatusVal = (value?: string) => {
  switch (value) {
    case 'pending':
      return OrderStatus.pending.valueOf();
    case 'active':
      return OrderStatus.active.valueOf();
    case 'matched':
      return OrderStatus.matched.valueOf();
    case 'executing':
      return OrderStatus.executing.valueOf();
    case 'executed':
      return OrderStatus.executed.valueOf();
    case 'canceled':
      return OrderStatus.canceled.valueOf();
    default:
      return undefined;
  }
};

export const getOrderStatusStr = (value?: OrderStatus) => {
  switch (value) {
    case OrderStatus.pending.valueOf():
      return 'Waiting for approval';
    case OrderStatus.active.valueOf():
      return 'Active';
    case OrderStatus.matched.valueOf():
      return 'Matched';
    case OrderStatus.executing.valueOf():
      return 'Executing';
    case OrderStatus.executed.valueOf():
      return 'Completed';
    case OrderStatus.canceled.valueOf():
      return 'Canceled';
    default:
      return '';
  }
};

export const getOrderStatusDescription = (value?: OrderStatus) => {
  switch (value) {
    case OrderStatus.pending.valueOf():
      return 'We are currently reviewing your order and will notify you upon approval.';
    case OrderStatus.active.valueOf():
      return 'Your order has been approved and is now active on the marketplace.';
    case OrderStatus.matched.valueOf():
      return 'There is a possible match for your order.';
    case OrderStatus.executing.valueOf():
      return 'We are finalizing the trade.';
    case OrderStatus.executed.valueOf():
      return 'Your order has been executed and closed successfully.';
    case OrderStatus.canceled.valueOf():
      return 'Your order has been canceled.';
    default:
      return '';
  }
};

export const getTradeStatusVal = (value?: string) => {
  switch (value) {
    case 'pending':
      return TradeStatus.pending.valueOf();
    case 'executed':
      return TradeStatus.executed.valueOf();
    case 'canceled':
      return TradeStatus.canceled.valueOf();
    default:
      return undefined;
  }
};

export const getTradeStatusStr = (value?: TradeStatus) => {
  switch (value) {
    case TradeStatus.pending.valueOf():
      return 'Pending';
    case TradeStatus.pending_executing.valueOf():
    case TradeStatus.executing.valueOf():
      return 'Executing';
    case TradeStatus.pending_executed.valueOf():
      return 'Closing';
    case TradeStatus.executed.valueOf():
      return 'Closed';
    case TradeStatus.canceled.valueOf():
      return 'Canceled';
    default:
      return '';
  }
};

export const getTradeStatusDescription = (value?: TradeStatus) => {
  switch (value) {
    case TradeStatus.pending.valueOf():
      return 'The trade is pending your review and approval.';
    case TradeStatus.pending_executing.valueOf():
    case TradeStatus.executing.valueOf():
      return 'The trade is waiting to be completed.';
    case TradeStatus.pending_executed.valueOf():
      return 'The trade is waiting to be closed.';
    case TradeStatus.executed.valueOf():
      return 'The trade has been executed and closed.';
    case TradeStatus.canceled.valueOf():
      return 'The trade has been canceled.';
    default:
      return '';
  }
};

export const getOfferingStatusVal = (value?: string) => {
  switch (value) {
    case 'draft':
      return OfferingStatus.draft.valueOf();
    case 'invest':
      return OfferingStatus.active.valueOf();
    case 'disabled':
      return OfferingStatus.disabled.valueOf();
    default:
      return undefined;
  }
};

export const getOfferingStatusStr = (value?: OfferingStatus) => {
  switch (value) {
    case OfferingStatus.draft.valueOf():
      return 'Draft';
    case OfferingStatus.active.valueOf():
      return 'Active';
    case OfferingStatus.disabled.valueOf():
      return 'Disabled';
    default:
      return '';
  }
};

export const getOfferingTypeVal = (value?: string) => {
  switch (value) {
    case 'primary':
      return OfferingType.primary.valueOf();
    case 'secondary':
      return OfferingType.secondary.valueOf();
    case 'promotion':
      return OfferingType.promotion.valueOf();
    default:
      return undefined;
  }
};

export const getOfferingTypeStr = (value?: OfferingType) => {
  switch (value) {
    case OfferingType.primary.valueOf():
      return 'Primary';
    case OfferingType.secondary.valueOf():
      return 'Secondary';
    case OfferingType.promotion.valueOf():
      return 'Promotional';
    default:
      return '';
  }
};

export const getVisibilityTypeStr = (value?: VisibilityType) => {
  switch (value) {
    case VisibilityType.default.valueOf():
      return 'Default';
    case VisibilityType.permission.valueOf():
      return 'Permission';
    case VisibilityType.hidden.valueOf():
      return 'Hidden';
    case VisibilityType.public.valueOf():
      return 'Public';
    default:
      return '';
  }
};

export const getEventStatusVal = (value?: string) => {
  switch (value) {
    case 'draft':
      return EventStatus.draft.valueOf();
    case 'scheduled':
      return EventStatus.scheduled.valueOf();
    case 'sent':
      return EventStatus.sent.valueOf();
    case 'archived':
      return EventStatus.archived.valueOf();
    case 'deleted':
      return EventStatus.deleted.valueOf();
    default:
      return undefined;
  }
};

export const getEventStatusStr = (value?: EventStatus) => {
  switch (value) {
    case EventStatus.draft.valueOf():
      return 'Draft';
    case EventStatus.scheduled.valueOf():
      return 'Scheduled';
    case EventStatus.sent.valueOf():
      return 'Sent';
    case EventStatus.archived.valueOf():
      return 'Archived';
    case EventStatus.deleted.valueOf():
      return 'Deleted';
    default:
      return '';
  }
};

export const getImportStatusStr = (value?: ImportStatus) => {
  switch (value) {
    case ImportStatus.skipped.valueOf():
      return 'Skipped';
    case ImportStatus.added.valueOf():
      return 'Added';
    case ImportStatus.deleted.valueOf():
      return 'Deleted';
    case ImportStatus.updated.valueOf():
      return 'Updated';
    default:
      return '';
  }
};

export const formatValueStr = (value?: string | number | boolean, type?: string) => {
  if (type == 'percentage' && isNumeric(value)) {
    return toPercentageFormat({ value: value });
  }
  if (type == 'number' && isNumeric(value)) {
    return formatNumber({ value: value });
  }
  if (type == 'currency' && isNumeric(value)) {
    return formatAmount(value);
  }
  if (type == 'multiply' && isNumeric(value)) {
    return `${formatNumber({ value: value })}x`;
  }
  if (type == 'date' && isNumeric(value)) {
    return formatDate(new Date(Number(value)), FULL_DATE_FORMAT);
  }
  return isNumeric(value) ? String(roundToOne(Number(value))) : String(value);
};

export const formatValueElement = (
  title?: string,
  value?: string | number | boolean,
  type?: string
) => {
  if (type == 'percentage' && isNumeric(value)) {
    return <span>{toPercentageFormat({ value: value })}</span>;
  }
  if (type == 'number' && isNumeric(value)) {
    return <span>{formatNumber({ value: value })}</span>;
  }
  if (type == 'currency' && isNumeric(value)) {
    return <span>{formatAmount(value)}</span>;
  }
  if (type == 'multiply' && isNumeric(value)) {
    return <span>{formatNumber({ value: value })}x</span>;
  }
  if (type == 'date' && isNumeric(value)) {
    return <span>{formatDate(new Date(Number(value)), FULL_DATE_FORMAT)}</span>;
  }
  if (value == 'V') {
    return <img src={`/svg/rating-check.svg`} alt={title} />;
  }
  if (value == 'A') {
    return <img src={`/svg/rating-A.svg`} alt={title} />;
  }
  return <span>{isNumeric(value) ? roundToOne(Number(value)) : value}</span>;
};

export const parseOfferingResponse = (requestData?: IOffering[]) => {
  return requestData
    ? requestData?.map((item) => {
        return calcInvestmentDetails({
          ...item,
          isSelected: false,
          isActive: false,
          description: item.offering_description,
          gallery_media: getGalleryItems(item.media)
        }) as IOfferingBrowsingData;
      })
    : [];
};

export const getOfferingFields = (offeringInfo: IOfferingInfo): OfferingItem[] => {
  const offeringFields = offeringInfo?.marketplace_metrics?.map((item) =>
    getOfferingField(offeringInfo, item)
  );

  if (offeringFields) {
    return offeringFields as OfferingItem[];
  }

  if (offeringInfo?.offering_type == OfferingType.promotion) {
    return [
      getOfferingField(offeringInfo, 'acquisition_cap_rate'),
      getOfferingField(offeringInfo, 'hold_date')
    ] as OfferingItem[];
  }

  if (offeringInfo?.offering_type == OfferingType.primary) {
    return [
      getOfferingField(offeringInfo, 'acquisition_cap_rate'),
      getOfferingField(offeringInfo, 'hold_date')
    ] as OfferingItem[];
  }

  return [
    getOfferingField(offeringInfo, 'implied_cap_rate'),
    getOfferingField(offeringInfo, 't12_yield')
  ] as OfferingItem[];
};

export const getPromoFields = (
  offeringInfo: IOfferingInfo,
  offeringData?: IOfferingData
): OfferingItem[] => {
  return offeringInfo?.offering_promo_metrics?.length
    ? offeringInfo?.offering_promo_metrics?.map((item) =>
        getOfferingDataField(offeringInfo, offeringData, item)
      )
    : offeringInfo?.offering_type == OfferingType.promotion
    ? [
        getOfferingDataField(offeringInfo, offeringData, 'acquisition_cap_rate'),
        getOfferingDataField(offeringInfo, offeringData, 'hold_date')
      ]
    : offeringInfo?.offering_type == OfferingType.primary
    ? [
        getOfferingDataField(offeringInfo, offeringData, 'acquisition_cap_rate'),
        getOfferingDataField(offeringInfo, offeringData, 'hold_date')
      ]
    : [
        getOfferingDataField(offeringInfo, offeringData, 't12_yield'),
        getOfferingDataField(offeringInfo, offeringData, 'implied_cap_rate'),
        getOfferingDataField(offeringInfo, offeringData, 'hold_date')
      ];
};

export const getOrderFields = (order: IOrder): OfferingItem[] => {
  if (order.order_type == OrderType.int_buy) {
    return [
      {
        title: 'Investment Amount',
        value: formatAmount(order.total, order?.currency, 0)
      }
    ];
  }

  return [
    {
      title: 'Holdings in Issuer',
      value: formatHoldingsInIssuer(order.quantity, order.offering_quantity)
    },
    {
      title: 'Premium',
      value: formatSharePremium(order.price, order.offering_price)
    }
  ];
};

export const getOfferingTag = (offeringInfo?: IOfferingInfo) => {
  return isNotEmpty(offeringInfo?.tag)
    ? {
        label: offeringInfo?.tag?.label,
        type: offeringInfo?.tag?.type,
        color: offeringInfo?.tag?.color || '#ffffff',
        background: offeringInfo?.tag?.background || '#8AC13F'
      }
    : offeringInfo?.count_pending_buy || offeringInfo?.count_pending_sell
    ? {
        label: 'Active Orders',
        color: '#ffffff',
        background: '#F39C12'
      }
    : undefined;
};

export const isBuyEnabled = (offeringInfo?: IOfferingInfo, authDetails?: IAuthDetails) => {
  if (!authDetails?.isApproved) return false;
  if (authDetails?.isManagement) return false;
  if (offeringInfo?.offering_status != OfferingStatus.active) return false;
  if (!offeringInfo?.name && !offeringInfo?.offering_name) return false;

  // if secondary, make sure price is enabled
  if (
    offeringInfo?.offering_type == OfferingType.secondary &&
    !offeringInfo?.price &&
    !offeringInfo?.offering_price
  )
    return false;

  // otherwise return true
  return true;
};

export const isSellEnabled = (offeringInfo?: IOfferingInfo, authDetails?: IAuthDetails) => {
  if (!authDetails?.isApproved) return false;
  if (authDetails?.isManagement) return false;
  if (offeringInfo?.offering_status != OfferingStatus.active) return false;
  if (!offeringInfo?.name && !offeringInfo?.offering_name) return false;

  // if not secondary return false
  if (offeringInfo?.offering_type != OfferingType.secondary) return false;

  // if no holdings, return false
  if (!offeringInfo?.volume_holding_investor || offeringInfo?.volume_holding_investor <= 0)
    return false;

  // if secondary, make sure price is enabled
  if (!offeringInfo?.price && !offeringInfo?.offering_price) return false;

  // otherwise return true
  return true;
};

export const getFieldComment = (
  offeringData?: IOfferingData,
  fieldName?: string,
  defVal?: string
) => {
  return getObjProperty(offeringData, `${fieldName}_comment`, defVal);
};

export const getFieldTitle = (
  offeringData?: IOfferingData,
  fieldName?: string,
  defVal?: string
) => {
  return getObjProperty(offeringData, `${fieldName}_title`, defVal);
};

export const getFieldShortTitle = (
  offeringData?: IOfferingData,
  fieldName?: string,
  defVal?: string
) => {
  return getObjProperty(offeringData, `${fieldName}_short_title`, defVal);
};

export const getLastValueIndicationField = (
  offeringInfo?: IOfferingInfo,
  offeringData?: IOfferingData
): OfferingItem => {
  if (offeringInfo?.appraisal_valuation) {
    return {
      title: getFieldTitle(offeringData, 'appraisal_valuation', 'Last Price Indication'),
      shortTitle: getFieldShortTitle(offeringData, 'appraisal_valuation', 'Last Price Indication'),
      description: getFieldComment(
        offeringData,
        'appraisal_valuation',
        `Based on the appraisal valuation${
          offeringInfo?.appraisal_valuation_date
            ? ' from ' + formatDate(new Date(offeringInfo?.appraisal_valuation_date), 'MMMM YYYY')
            : ''
        }.`
      ),
      value: getValue(offeringInfo?.appraisal_valuation, (val) =>
        formatValuation(val, offeringInfo?.currency)
      ),
      range: getRangeValue(
        offeringInfo?.appraisal_valuation,
        offeringInfo?.appraisal_valuation_top,
        (val) => formatValuation(val, offeringInfo?.currency)
      )
    };
  }

  if (offeringInfo?.bov_valuation) {
    return {
      title: getFieldTitle(offeringData, 'bov_valuation', 'Last Price Indication'),
      shortTitle: getFieldShortTitle(offeringData, 'bov_valuation', 'Last Price Indication'),
      description: getFieldComment(
        offeringData,
        'bov_valuation',
        `Based on the Broker Opinion of Value (BOV)${
          offeringInfo?.bov_valuation_date
            ? ' from ' + formatDate(new Date(offeringInfo?.bov_valuation_date), 'MMMM YYYY')
            : ''
        }.`
      ),
      value: getValue(offeringInfo?.bov_valuation, (val) =>
        formatValuation(val, offeringInfo?.currency)
      ),
      range: getRangeValue(offeringInfo?.bov_valuation, offeringInfo?.bov_valuation_top, (val) =>
        formatValuation(val, offeringInfo?.currency)
      )
    };
  }

  return {
    title: '',
    shortTitle: '',
    description: '',
    value: '',
    range: ''
  };
};

export const getOfferingField = (offeringInfo?: IOfferingInfo, field?: string): OfferingItem => {
  return getOfferingDataField(offeringInfo, offeringInfo, field) as Record<string, string>;
};

export const getOfferingDataField = (
  offeringInfo?: IOfferingInfo,
  offeringData?: IOfferingData,
  field?: string
): OfferingItem => {
  switch (field) {
    case 'acquisition_price':
      return offeringInfo?.recap_price
        ? getOfferingDataField(offeringInfo, offeringData, 'recap_price')
        : offeringInfo?.appraisal_valuation &&
          offeringInfo?.acquisition_price &&
          offeringInfo?.appraisal_valuation > offeringInfo?.acquisition_price
        ? getOfferingDataField(offeringInfo, offeringData, 'appraisal_valuation')
        : {
            title: getFieldTitle(offeringData, 'acquisition_price', 'Acquisition Price'),
            shortTitle: getFieldShortTitle(offeringData, 'acquisition_price', 'Acquisition Price'),
            description: getFieldComment(
              offeringData,
              'acquisition_price',
              `Based on the acquisition price${
                offeringInfo?.acquisition_date
                  ? ' from ' + formatDate(new Date(offeringInfo?.acquisition_date), 'MMMM YYYY')
                  : ''
              }.`
            ),
            value: getValue(offeringInfo?.acquisition_price, (val) =>
              formatValuation(val, offeringInfo?.currency)
            ),
            range: getRangeValue(
              offeringInfo?.acquisition_price,
              offeringInfo?.acquisition_price_top,
              (val) => formatValuation(val, offeringInfo?.currency)
            )
          };
    case 'acquisition_cap_rate':
      return offeringInfo?.stabilized_cap_rate
        ? getOfferingDataField(offeringInfo, offeringData, 'stabilized_cap_rate')
        : offeringInfo?.recap_cap_rate
        ? getOfferingDataField(offeringInfo, offeringData, 'recap_cap_rate')
        : {
            title: getFieldTitle(offeringData, 'acquisition_cap_rate', 'Acquisition Cap Rate'),
            shortTitle: getFieldShortTitle(offeringData, 'acquisition_cap_rate', 'Acquisition Cap'),
            description: getFieldComment(
              offeringData,
              'acquisition_cap_rate',
              `Based on the last T12 NOI and the acquisition price${
                offeringInfo?.t12_noi_date
                  ? ' as of ' + formatDate(new Date(offeringInfo?.t12_noi_date), 'MMMM YYYY')
                  : ''
              }.`
            ),
            value: getValue(offeringInfo?.acquisition_cap_rate, (val) =>
              toPercentageFixedFormat({ value: val })
            ),
            range: getRangeValue(
              offeringInfo?.acquisition_cap_rate,
              offeringInfo?.acquisition_cap_rate_top,
              (val) => toPercentageFixedFormat({ value: val, digits: 0 })
            )
          };
    case 'appraisal_valuation':
      return {
        title: getFieldTitle(offeringData, 'appraisal_valuation', 'Appraisal Price'),
        shortTitle: getFieldShortTitle(offeringData, 'appraisal_valuation', 'Appraisal Price'),
        description: getFieldComment(
          offeringData,
          'appraisal_valuation',
          `Based on the appraisal price${
            offeringInfo?.appraisal_valuation_date
              ? ' from ' + formatDate(new Date(offeringInfo?.appraisal_valuation_date), 'MMMM YYYY')
              : ''
          }.`
        ),
        value: getValue(offeringInfo?.appraisal_valuation, (val) =>
          formatValuation(val, offeringInfo?.currency)
        ),
        range: getRangeValue(
          offeringInfo?.appraisal_valuation,
          offeringInfo?.appraisal_valuation_top,
          (val) => formatValuation(val, offeringInfo?.currency)
        )
      };
    case 'recap_price':
      return {
        title: getFieldTitle(offeringData, 'recap_price', 'Recap Price'),
        shortTitle: getFieldShortTitle(offeringData, 'recap_price', 'Recap Price'),
        description: getFieldComment(
          offeringData,
          'recap_price',
          `Based on the recap price${
            offeringInfo?.recap_date
              ? ' from ' + formatDate(new Date(offeringInfo?.recap_date), 'MMMM YYYY')
              : ''
          }.`
        ),
        value: getValue(offeringInfo?.recap_price, (val) =>
          formatValuation(val, offeringInfo?.currency)
        ),
        range: getRangeValue(offeringInfo?.recap_price, offeringInfo?.recap_price_top, (val) =>
          formatValuation(val, offeringInfo?.currency)
        )
      };
    case 'recap_cap_rate':
      return {
        title: getFieldTitle(offeringData, 'recap_cap_rate', 'Recap Cap Rate'),
        shortTitle: getFieldShortTitle(offeringData, 'recap_cap_rate', 'Recap Cap'),
        description: getFieldComment(
          offeringData,
          'recap_cap_rate',
          `Based on the last T12 NOI and the recap value${
            offeringInfo?.t12_noi_date
              ? ' as of ' + formatDate(new Date(offeringInfo?.t12_noi_date), 'MMMM YYYY')
              : ''
          }.`
        ),
        value: getValue(offeringInfo?.recap_cap_rate, (val) =>
          toPercentageFixedFormat({ value: val })
        ),
        range: getRangeValue(
          offeringInfo?.recap_cap_rate,
          offeringInfo?.recap_cap_rate_top,
          (val) => toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'stabilized_cap_rate':
      return {
        title: getFieldTitle(offeringData, 'stabilized_cap_rate', 'Stabilized Cap Rate'),
        shortTitle: getFieldShortTitle(offeringData, 'stabilized_cap_rate', 'Stabilized Cap'),
        description: getFieldComment(
          offeringData,
          'stabilized_cap_rate',
          `Based on the stabilized NOI and the total cost of the development.'
              }.`
        ),
        value: getValue(offeringInfo?.stabilized_cap_rate, (val) =>
          toPercentageFixedFormat({ value: val })
        ),
        range: getRangeValue(
          offeringInfo?.stabilized_cap_rate,
          offeringInfo?.stabilized_cap_rate_top,
          (val) => toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'last_price_indication':
      return getLastValueIndicationField(offeringInfo, offeringData);
    case 'implied_cap_rate':
      return {
        title: getFieldTitle(offeringData, field, 'Implied Cap'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Implied Cap'),
        description: getFieldComment(
          offeringData,
          field,
          `Based on the last T12 NOI and the acquisition price${
            offeringInfo?.t12_noi_date
              ? ' as of ' + formatDate(new Date(offeringInfo?.t12_noi_date), 'MMMM YYYY')
              : ''
          }.`
        ),
        value: getValue(offeringInfo?.implied_cap_rate, (val) =>
          toPercentageFixedFormat({ value: val })
        ),
        range: getRangeValue(
          offeringInfo?.implied_cap_rate,
          offeringInfo?.implied_cap_rate_top,
          (val) => toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'return_on_cost':
      return {
        title: getFieldTitle(offeringData, field, 'Return on Cost'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Return on Cost'),
        description: getFieldComment(offeringData, field, 'Based on the stabilized NOI.'),
        value: getValue(
          offeringInfo?.return_on_cost,
          (val) => toPercentageFixedFormat({ value: val }),
          ''
        ),
        range: getRangeValue(
          offeringInfo?.return_on_cost,
          offeringInfo?.return_on_cost_top,
          (val) => toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'target_irr':
      return {
        title: getFieldTitle(offeringData, field, 'Target IRR'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Target IRR'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: getValue(offeringInfo?.target_irr, (val) => toPercentageFixedFormat({ value: val })),
        range: getRangeValue(offeringInfo?.target_irr, offeringInfo?.target_irr_top, (val) =>
          toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'target_yield':
      return {
        title: getFieldTitle(offeringData, field, 'Target Avg. CoC'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Target Avg. CoC'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: getValue(offeringInfo?.target_yield, (val) =>
          toPercentageFixedFormat({ value: val })
        ),
        range: getRangeValue(offeringInfo?.target_yield, offeringInfo?.target_yield_top, (val) =>
          toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'target_equity_multiple':
      return {
        title: getFieldTitle(offeringData, field, 'Target Equity Multiple'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Target Equity Multiple'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: getValue(offeringInfo?.target_equity_multiple, (val) => `${val}x`),
        range: getRangeValue(
          offeringInfo?.target_equity_multiple,
          offeringInfo?.target_equity_multiple_top,
          (val) => `${val}x`
        )
      };
    case 'price':
      return {
        title: getFieldTitle(offeringData, field, 'Share Price'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Share Price'),
        description: getFieldComment(
          offeringData,
          field,
          `The share price is based on the equity at acquisition divided by ${offeringInfo?.quantity} shares.`
        ),
        value: getValue(offeringInfo?.price, (val) => formatSharePrice(val, offeringInfo?.currency))
      };
    case 'hold_date':
      return {
        title:
          offeringInfo?.offering_type == OfferingType.secondary
            ? getFieldTitle(offeringData, 'hold_date', 'Target Remaining Hold Period')
            : getFieldTitle(offeringData, 'hold_date', 'Target Hold Period'),
        shortTitle: getFieldShortTitle(offeringData, 'hold_date', 'Target Hold Period'),
        description: getFieldComment(
          offeringData,
          'hold_date',
          "Based on the Sponsor's business plan."
        ),
        value: getValue(offeringInfo?.hold_date, (val) => formatHoldPeriod(val) || ''),
        range: getRangeValue(
          offeringInfo?.hold_date,
          offeringInfo?.hold_date_top,
          (val) => formatHoldPeriod(val) || ''
        )
      };
    case 't12_yield':
      return {
        title:
          offeringInfo?.offering_type == OfferingType.secondary
            ? getFieldTitle(offeringData, field, 'Cash Yield')
            : getFieldTitle(offeringData, field, 'Projected Cash Yield'),
        shortTitle:
          offeringInfo?.offering_type == OfferingType.secondary
            ? getFieldShortTitle(offeringData, field, 'Cash Yield')
            : getFieldShortTitle(offeringData, field, 'Projected Cash Yield'),
        description: getFieldComment(
          offeringData,
          field,
          `Calculated by dividing the last T12 cash flow distribution by the discounted equity${
            offeringInfo?.t12_noi_date
              ? ' as of ' + formatDate(new Date(offeringInfo?.t12_noi_date), 'MMMM YYYY')
              : ''
          }.`
        ),
        value: getValue(offeringInfo?.t12_yield, (val) => toPercentageFixedFormat({ value: val })),
        range: getRangeValue(offeringInfo?.t12_yield, offeringInfo?.t12_yield_top, (val) =>
          toPercentageFixedFormat({ value: val, digits: 0 })
        )
      };
    case 'total_dividends_investor':
      return {
        title: getFieldTitle(offeringData, field, 'Distributions Received'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Distributions Received'),
        description: getFieldComment(offeringData, field, ''),
        value: getValue(offeringInfo?.total_dividends_investor, (val) =>
          formatAmount(val, offeringInfo?.currency)
        )
      };
    case 't12_yield_investor':
      return {
        title: getFieldTitle(offeringData, field, 'My Cash Yield'),
        shortTitle: getFieldShortTitle(offeringData, field, 'My Cash Yield'),
        description: getFieldComment(offeringData, field, ''),
        value: getValue(offeringInfo?.t12_yield_investor, (val) =>
          toPercentageFixedFormat({ value: val })
        )
      };
    case 'premium':
      return {
        title: getFieldTitle(offeringData, field, 'Premium'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Premium'),
        description: getFieldComment(
          offeringData,
          field,
          offeringInfo?.recap_price
            ? `Premium to the recap price${
                offeringInfo?.recap_date
                  ? ' from ' + formatDate(new Date(offeringInfo?.recap_date), 'MMMM YYYY')
                  : ''
              }.`
            : `Premium to the acquisition price${
                offeringInfo?.acquisition_date
                  ? ' from ' + formatDate(new Date(offeringInfo?.acquisition_date), 'MMMM YYYY')
                  : ''
              }.`
        ),
        value: ''
      };
    case 'target_equity':
      return {
        title: getFieldTitle(offeringData, field, 'Target Equity'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Target Equity'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: getValue(offeringInfo?.target_equity, (val) =>
          formatValuation(val, offeringInfo?.currency)
        ),
        range: getRangeValue(offeringInfo?.target_equity, offeringInfo?.target_equity_top, (val) =>
          formatValuation(val, offeringInfo?.currency)
        )
      };
    case 'target_aum_size':
      return {
        title: getFieldTitle(offeringData, field, 'Target AUM Size'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Target AUM Size'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: getValue(offeringInfo?.target_aum_size, (val) =>
          formatValuation(val, offeringInfo?.currency)
        ),
        range: getRangeValue(
          offeringInfo?.target_aum_size,
          offeringInfo?.target_aum_size_range,
          (val) => formatValuation(val, offeringInfo?.currency)
        )
      };
    case 'strategy':
      return {
        title: getFieldTitle(offeringData, field, 'Strategy'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Strategy'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: offeringInfo?.strategy
      };
    case 'investment_types':
      return {
        title: getFieldTitle(offeringData, field, 'Investment Types'),
        shortTitle: getFieldShortTitle(offeringData, field, 'Investment Types'),
        description: getFieldComment(offeringData, field, "Based on the Sponsor's business plan."),
        value: offeringInfo?.investment_types
      };
    default:
      return {
        title: '',
        shortTitle: '',
        description: '',
        value: '',
        range: ''
      };
  }
};

export const canWatchOffering = (
  offering: IOfferingInfo,
  authenticated: boolean | undefined,
  management: boolean | undefined
) => authenticated && !management && !!offering?.offering_name;

export const getReportStatusVal = (value?: string) => {
  switch (value) {
    case 'manual':
      return ReportStatus.manual.valueOf();
    case 'scheduled':
      return ReportStatus.scheduled.valueOf();
    case 'recurring':
      return ReportStatus.recurring.valueOf();
    case 'archived':
      return ReportStatus.archived.valueOf();
    case 'deleted':
      return ReportStatus.deleted.valueOf();
    default:
      return undefined;
  }
};

export const getReportStatusStr = (value?: ReportStatus) => {
  switch (value) {
    case ReportStatus.manual.valueOf():
      return 'Manual';
    case ReportStatus.scheduled.valueOf():
      return 'Scheduled';
    case ReportStatus.recurring.valueOf():
      return 'Recurring';
    case ReportStatus.archived.valueOf():
      return 'Archived';
    case ReportStatus.deleted.valueOf():
      return 'Deleted';
    default:
      return '';
  }
};

export const getReportTypeStr = (value?: string) => {
  switch (value) {
    case ReportType.registered_users.valueOf():
      return 'Registered Users';
    case ReportType.registered_users_kyc.valueOf():
      return 'Registered Users - Investment Preferences';
    case ReportType.user_permissions.valueOf():
      return 'User Permissions';
    case ReportType.all_orders.valueOf():
      return 'All Orders';
    case ReportType.active_orders.valueOf():
      return 'Active Orders';
    case ReportType.executed_orders.valueOf():
      return 'Executed Orders';
    case ReportType.canceled_orders.valueOf():
      return 'Canceled Orders';
    case ReportType.all_trades.valueOf():
      return 'All Trades';
    case ReportType.active_trades.valueOf():
      return 'Active Trades';
    case ReportType.executed_trades.valueOf():
      return 'Executed Trades';
    case ReportType.canceled_trades.valueOf():
      return 'Canceled Trades';
    default:
      return 'Custom';
  }
};

export const getReportIntervalVal = (value?: string) => {
  switch (value) {
    case 'once':
      return ReportInterval.once.valueOf();
    case 'daily':
      return ReportInterval.daily.valueOf();
    case 'weekly':
      return ReportInterval.weekly.valueOf();
    case 'monthly':
      return ReportInterval.monthly.valueOf();
    default:
      return undefined;
  }
};

export const getReportIntervalStr = (value?: ReportInterval) => {
  switch (value) {
    case ReportInterval.once.valueOf():
      return 'Once';
    case ReportInterval.daily.valueOf():
      return 'Daily';
    case ReportInterval.weekly.valueOf():
      return 'Weekly';
    case ReportInterval.monthly.valueOf():
      return 'Monthly';
    default:
      return 'Once';
  }
};

export const getReportStatusFormatted = (report?: IReport) => {
  switch (report?.report_status) {
    case ReportStatus.manual:
      return (
        <Typography variant="inherit" color="primary.main">
          {`Manual ${report?.executed_at ? ' - Sent' : ''}`}
        </Typography>
      );
    case ReportStatus.scheduled:
      return (
        <Typography variant="inherit" color="primary.main">
          {`Occurrence ${report?.executed_at ? ' - Sent' : ''}`}
        </Typography>
      );
    case ReportStatus.recurring:
      return (
        <Typography variant="inherit" color="success.main">
          {'Recurring'}
        </Typography>
      );
    case ReportStatus.archived:
      return (
        <Typography variant="inherit" color="text.secondary">
          {`Archived ${report?.executed_at ? ' - Sent' : ''}`}
        </Typography>
      );
    case ReportStatus.deleted:
      return (
        <Typography variant="inherit" color="error.main">
          {`Deleted ${report?.executed_at ? ' - Sent' : ''}`}
        </Typography>
      );
  }
};

export const formatHoldingName = (holding: IHolding) => {
  return `${
    holding.offering_name
      ? `${holding.offering_name} (${holding.offering_id})`
      : holding.offering_id
  } - ${formatAmount(holding.total, holding.currency)}${
    holding.quantity
      ? ` (${formatQuantity(holding.quantity)}*${formatSharePrice(holding.price)})`
      : ''
  }${
    holding.holding_date
      ? ` – ${formatDate(new Date(holding.holding_date), FULL_DATE_TIME_FORMAT)}`
      : ''
  }`;
};

export const formatHoldingOptions = (holdings?: Array<IHolding>): SelectOption[] => {
  return holdings
    ? holdings
        .filter((holding) => holding.id)
        .map((holding) => ({
          value: holding.id || '',
          name: formatHoldingName(holding)
        }))
    : [];
};

export const formatSponsorOptions = (sponsors?: Array<ISponsor>): SelectOption[] => {
  return sponsors
    ? sponsors
        .filter((sponsor) => sponsor.id)
        .map((sponsor) => ({
          value: sponsor.id || '',
          name: `${sponsor.id} - ${sponsor.name}`
        }))
    : [];
};

export const formatOfferingOptions = (offerings?: Array<IOffering>): SelectOption[] => {
  return offerings
    ? offerings
        .filter((offering) => offering.id)
        .map((offering) => ({
          value: offering.id || '',
          name: `${offering.id} - ${offering.name}`
        }))
    : [];
};

export const buildResourceGallery = (media: Array<IResourceData>) => {
  return media
    .filter((image) => image?.url)
    .map((image) => ({
      original: getValidUrl(image?.url),
      thumbnail: getValidUrl(image?.url),
      title: image?.description || image?.name
    }))
    .filter((item) => item.original);
};

export const getResourceGallery = (obj: Record<string, unknown>, path: string) => {
  const media = getObjProperty(obj, path) as Array<IResourceData>;
  if (media) {
    return buildResourceGallery(media);
  }
};

export const isLimitedOffering = (offeringInfo?: IOfferingInfo) => {
  return !offeringInfo?.acquisition_price && !offeringInfo?.hold_date && !offeringInfo?.price;
};
